import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import shortlistApi from '@modules/shortlist/api'
import { ShortListCustomFieldOption } from '@modules/shortlist/types'
import { Autocomplete, Box, TextField } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface CustomColumnDropdownProps {
  shortlistId: number
  profileId: number
  customFieldId: number
  availableOptions?: ShortListCustomFieldOption[]
  columnName: string
  selectedOptionId: string
  onOptionSelect: (profileId: number, customFieldId: number, value: string) => void
}

const CustomColumnDropdown = ({
  shortlistId,
  profileId,
  customFieldId,
  availableOptions,
  columnName,
  selectedOptionId,
  onOptionSelect,
}: CustomColumnDropdownProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [optimisticId, setOptimisticId] = useState('')

  // TODO: move this business/api logic outside of the component
  const { mutate: updateShortlistProfileById } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfileById.fn,
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, shortlistId] })
    },
  })

  const handleSelectOption = useCallback(
    (option?: ShortListCustomFieldOption | null) => {
      if (selectedOptionId !== option?.id) {
        updateShortlistProfileById({
          id: profileId,
          data: { customFieldValues: { [customFieldId]: option?.id as string } },
        })

        setOptimisticId(option?.id as string)
      }
    },
    [selectedOptionId, profileId, customFieldId, onOptionSelect, updateShortlistProfileById],
  )

  const selectedOption = useMemo(
    () =>
      availableOptions?.find(i => i.id === (optimisticId || selectedOptionId)) ?? {
        value: t(translationKeys.NOT_SET),
        id: t(translationKeys.NOT_SET),
      },
    [selectedOptionId, availableOptions, optimisticId],
  )

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        value={selectedOption}
        options={availableOptions ?? []}
        getOptionLabel={option => option.value}
        onChange={(_, newValue) => handleSelectOption(newValue)}
        clearIcon={null}
        isOptionEqualToValue={(a, b) => a.id === b.id || b.value === t(translationKeys.NOT_SET)}
        // eslint-disable-next-line react/jsx-props-no-spreading
        renderInput={params => <TextField {...params} label={columnName} variant="outlined" size="small" fullWidth />}
        sx={{
          width: '100%',
          minWidth: '150px',
          '& .MuiAutocomplete-listbox': {
            overflowY: 'auto',
          },
        }}
      />
    </Box>
  )
}

export default memo(CustomColumnDropdown)
